import { useEffect, useRef, useState } from "react";

import { useReportIssueStream } from "@/api/use-report-issue-stream";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * `/report` — the M2 admin surface for filing GitHub issues against the
 * platform repo without leaving admin-ui (issue
 * [#287](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/287)).
 *
 * A small chat panel. The user types what they noticed; the model
 * either asks one clarifying question or files the issue. On success
 * the panel shows the issue URL and a "File another" button.
 *
 * The model is a substance gate, not a triage agent — it does not
 * search the codebase, dedupe, choose labels, or set Phase / Priority.
 * Those steps are done by a separate async triage routine.
 *
 * Per-user-per-day cap: 10 filings. Enforced server-side.
 */
export function ReportIssue() {
  const stream = useReportIssueStream();
  const [input, setInput] = useState("");
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the transcript to the bottom whenever a new message
  // lands or the streaming bubble grows. The check is cheap; React
  // batches the re-render so this only fires once per chunk arrival.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [stream.messages, stream.state]);

  const inputDisabled =
    stream.state === "streaming" || stream.state === "filed";
  const canSend =
    !inputDisabled && input.trim().length > 0;

  const submit = (): void => {
    if (!canSend) return;
    stream.send(input);
    setInput("");
  };

  return (
    <div className="space-y-6 max-w-[720px]">
      <header>
        <h1 className="text-2xl font-light tracking-tight">Report an issue</h1>
        <p className="mt-2 text-sm text-text-muted">
          Describe a bug or a feature request in plain English. I&apos;ll
          ask for clarification if needed, then file it on{" "}
          <code className="font-mono text-xs">prospect-platform</code>. A
          separate routine handles labels, milestone, and priority — you
          don&apos;t need to set those here.
        </p>
      </header>

      <Card className="p-0 overflow-hidden">
        <div
          ref={transcriptRef}
          className="min-h-[280px] max-h-[480px] overflow-y-auto px-5 py-4 space-y-3"
        >
          {stream.messages.length === 0 && stream.state === "idle" ? (
            <EmptyHint />
          ) : (
            stream.messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} />
            ))
          )}
          {stream.state === "filed" && stream.filedIssue ? (
            <FiledBanner issue={stream.filedIssue} />
          ) : null}
        </div>
        {stream.state === "errored" && stream.error ? (
          <div className="border-t border-border px-5 py-3">
            <ErrorBanner
              error={new Error(stream.error.message)}
              onRetry={() => stream.reset()}
            />
          </div>
        ) : null}
        <div className="border-t border-border bg-bg-dark/30 px-5 py-3">
          {stream.state === "filed" ? (
            <Button variant="default" onClick={() => stream.reset()}>
              File another
            </Button>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              className="flex items-end gap-3"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends; shift+Enter inserts a newline. The
                  // shape the model expects is short prose with the
                  // occasional repro line — multi-line input is rare
                  // but supported.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                disabled={inputDisabled}
                placeholder="Describe what happened, or what you&rsquo;d like to see…"
                rows={2}
                className={cn(
                  "flex-1 resize-none rounded border border-border bg-bg-dark px-3 py-2 text-sm",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  "disabled:opacity-50",
                )}
              />
              <Button type="submit" disabled={!canSend}>
                {stream.state === "streaming" ? "Sending…" : "Send"}
              </Button>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}

function EmptyHint(): JSX.Element {
  return (
    <div className="text-sm text-text-muted py-8 text-center space-y-2">
      <p>Type anything below to get started.</p>
      <p className="text-xs">
        Bugs, rough edges, feature requests — all welcome.
      </p>
    </div>
  );
}

function Bubble({
  role,
  text,
}: {
  role: "user" | "assistant";
  text: string;
}): JSX.Element {
  const isUser = role === "user";
  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "rounded-lg px-3 py-2 text-sm max-w-[80%] whitespace-pre-wrap",
          isUser
            ? "bg-accent/10 border border-accent/30 text-text"
            : "bg-bg-dark/60 border border-border text-text",
        )}
      >
        {text || (
          // Render a faint placeholder for the in-flight assistant
          // bubble before the first text_delta arrives, so the
          // streaming state has a visible affordance.
          <span className="text-text-muted italic">…</span>
        )}
      </div>
    </div>
  );
}

function FiledBanner({ issue }: { issue: { number: number; htmlUrl: string; title: string } }): JSX.Element {
  return (
    <div className="mt-2 rounded border border-accent/40 bg-accent/5 px-3 py-2 text-sm space-y-1">
      <p className="font-mono text-xs text-text-muted uppercase tracking-label">
        Filed
      </p>
      <p>
        <a
          href={issue.htmlUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline hover:no-underline"
        >
          #{issue.number} {issue.title}
        </a>
      </p>
    </div>
  );
}
